import { FormEvent, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, Case, CasePage, TimeEntry } from "../lib/api";
import { useAuth } from "../lib/auth";

interface Props {
  onClose: () => void;
  /** Pre-select this case in the picker. Useful when the modal is opened
   *  from inside a case view so the user doesn't have to re-pick it. */
  defaultCaseId?: number;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function TimeEntryModal({ onClose, defaultCaseId }: Props) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [entryDate, setEntryDate] = useState(todayISO());
  const [hours, setHours] = useState("");
  const [mins, setMins] = useState("");
  const [mileage, setMileage] = useState("");
  const [note, setNote] = useState("");
  const [caseId, setCaseId] = useState<number | "">(defaultCaseId ?? "");

  const [cases, setCases] = useState<Case[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void loadCases();
  }, []);

  async function loadCases() {
    try {
      // Time-entry modal needs the full picker list, not a page; ask for a
      // generous limit so any reasonable org fits in one round-trip.
      const page = await api.get<CasePage>("/cases?limit=500&status=active");
      setCases(page.items);
    } catch { /* non-critical */ }
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);

    const totalMinutes = (parseInt(hours || "0", 10) * 60) + parseInt(mins || "0", 10);
    if (totalMinutes < 1) {
      setErr("Enter at least 1 minute.");
      return;
    }

    const body: Record<string, unknown> = {
      entry_date: entryDate,
      minutes: totalMinutes,
      note: note.trim() || null,
      case_id: caseId || null,
    };
    if (mileage.trim()) body.mileage = parseFloat(mileage);

    setBusy(true);
    try {
      await api.post<TimeEntry>("/time-entries", body);
      setSaved(true);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to save entry");
    } finally {
      setBusy(false);
    }
  }

  if (saved) {
    return (
      <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
        <div
          className="modal__card modal__card--split"
          onClick={(e) => e.stopPropagation()}
        >
          <header className="modal__header">
            <div className="modal__header-main">
              <h2 className="modal__header-title">Entry saved</h2>
            </div>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </header>

          <div className="modal__body">
            <div className="te-success">
              <svg className="te-success__icon" viewBox="0 0 48 48" width="48" height="48" fill="none">
                <circle cx="24" cy="24" r="22" stroke="var(--color-accent)" strokeWidth="3" />
                <path d="M14 25l7 7 13-14" stroke="var(--color-accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <p className="te-success__msg">Your time entry has been saved successfully.</p>
            </div>
          </div>

          <footer className="modal__footer">
            <span className="muted modal__footer-hint">
              {user?.display_name} — {user?.role}
            </span>
            <div className="modal__footer-actions">
              <button type="button" className="btn" onClick={onClose}>Close</button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => { onClose(); navigate("/time"); }}
              >
                View time entries
              </button>
            </div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal__card modal__card--split"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <div className="modal__header-main">
            <h2 className="modal__header-title">Log time & mileage</h2>
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="modal__body">
          {err && <div className="alert alert--error">{err}</div>}

          <form id="te-form" onSubmit={onSubmit} className="te-form">
            <div className="te-form__row">
              <label className="te-form__field">
                <span>Date</span>
                <input
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  required
                />
              </label>
              <label className="te-form__field te-form__field--sm">
                <span>Hours</span>
                <input
                  type="number"
                  min="0"
                  max="24"
                  placeholder="0"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </label>
              <label className="te-form__field te-form__field--sm">
                <span>Minutes</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  placeholder="0"
                  value={mins}
                  onChange={(e) => setMins(e.target.value)}
                />
              </label>
              <label className="te-form__field te-form__field--sm">
                <span>Mileage</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="optional"
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value)}
                />
              </label>
            </div>
            <label className="te-form__field">
              <span>Case (optional)</span>
              <select value={caseId} onChange={(e) => setCaseId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">— none —</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label className="te-form__field">
              <span>Note</span>
              <textarea
                rows={2}
                maxLength={2000}
                placeholder="What did you work on?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </form>
        </div>

        <footer className="modal__footer">
          <span className="muted modal__footer-hint">
            {user?.display_name} — {user?.role}
          </span>
          <div className="modal__footer-actions">
            <button type="button" className="btn" onClick={onClose}>Close</button>
            <button type="submit" form="te-form" className="btn btn--primary" disabled={busy}>
              {busy ? "Saving…" : "Save entry"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
